import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BRAND } from "@/lib/constants";
import AddScraperDialog from "@/components/catalog/AddScraperDialog";
import SubscribeDialog from "@/components/catalog/SubscribeDialog";
import RequestPortalDialog from "@/components/catalog/RequestPortalDialog";
import EditCredentialsDialog from "@/components/catalog/EditCredentialsDialog";
import RenamePortalDialog from "@/components/catalog/RenamePortalDialog";
import api, { getErrorMessage } from "@/lib/api";
import PortalPauseToggle from "@/components/catalog/PortalPauseToggle";
import TwoFASendersPanel from "@/components/catalog/TwoFASendersPanel";
import { useConfirm } from "@/hooks/useConfirm";
import { useAuth } from "@/contexts/AuthContext";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function portalInitial(portal) {
  const raw = (portal.display_name || portal.name || portal.portal_name || portal.catalog_id || "?").trim();
  return raw[0] ? raw[0].toUpperCase() : "?";
}

function PortalLogo({ portal, size = 32 }) {
  const initial = portalInitial(portal);
  return (
    <div
      className="flex items-center justify-center rounded-md text-white font-semibold text-sm flex-shrink-0"
      style={{ width: size, height: size, backgroundColor: BRAND.bg }}
      aria-label={`${portal.display_name || portal.name || portal.portal_name || portal.catalog_id} logo placeholder`}
    >
      {initial}
    </div>
  );
}

function hostname(url) {
  if (!url) return "";
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
  }
}

/**
 * Format a date string as relative time (e.g. "2 hours ago").
 * Returns null if the input is falsy or unparseable.
 */
function relativeTime(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return formatDistanceToNow(d, { addSuffix: true });
  } catch {
    return null;
  }
}

// Subtext rendered under EVERY health chip that has ever succeeded — so ops
// triage can see "this portal has working creds, last good run was 3h ago"
// even on rows currently in failing / credentials_failing / two_fa_failing.
function LastSuccessLine({ ts }) {
  if (!ts) return null;
  const rel = relativeTime(ts);
  if (!rel) return null;
  return (
    <span className="text-xs text-gray-400 font-normal pl-3">
      Last success {rel}
    </span>
  );
}

// ---------------------------------------------------------------------------
// HealthChip — renders the right indicator per health.status
// ---------------------------------------------------------------------------

function HealthChip({ portal, onOpenEditCredentials }) {
  const health = portal.health;
  // "Awaiting build" should NOT render for custom_ rows that already
  // resolve to a canonical slug with a live scraper — the scraper IS
  // built, the Cosmos flag just never got flipped. Those rows fall
  // through to the real health rendering (ok / unknown / etc.).
  const isResolvedCustom =
    (portal.catalog_id || "").startsWith("custom_") &&
    portal.resolved_catalog_id &&
    portal.resolved_catalog_id !== portal.catalog_id;
  const isPendingConstruction =
    portal.status === "pending_construction" && !isResolvedCustom;

  // Blocked takes precedence over every other health signal. Derived
  // server-side from shared-collection inactive state (e.g. Apex —
  // account locked awaiting portal support unlock). The tooltip is
  // explicit about the operational consequence so the client
  // understands why we're not retrying.
  if (health && health.status === "blocked") {
    return (
      <span className="inline-flex flex-col">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-1 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5 cursor-default w-fit">
                <span aria-hidden="true">&#128274;</span> Portal locked
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>
                Blocked portals won&rsquo;t run until resolved in order not to
                further delay reconciliation.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <LastSuccessLine ts={health?.last_success_at} />
      </span>
    );
  }

  const credsPending =
    !!portal.credentials_updated_at &&
    (!portal.last_run_at ||
      new Date(portal.credentials_updated_at) > new Date(portal.last_run_at));
  if (credsPending && !portal.is_blocked && !portal.auth_paused_at) {
    return (
      <span data-testid="creds-saved" className="text-sm text-gray-500">
        Saved, we'll check it on the next run
      </span>
    );
  }

  if (portal.login_failed === true && !portal.is_blocked && !portal.auth_paused_at) {
    return (
      <button
        type="button"
        data-testid="login-failed"
        onClick={() => onOpenEditCredentials?.(portal)}
        className="text-sm font-medium text-red-600 underline-offset-2 hover:underline text-left"
      >
        Login failed, check your credentials
      </button>
    );
  }

  if (isPendingConstruction) {
    return (
      <span className="inline-flex items-center gap-1 text-sm text-gray-400">
        <span aria-hidden="true">&middot;</span> Awaiting build
      </span>
    );
  }

  if (!health || health.status === "unknown") {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-1 text-sm text-gray-400 cursor-default">
              <span aria-hidden="true">&middot;</span> No runs yet
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>Your first scrape will run within 24 hours.</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (health.status === "ok") {
    const rel = relativeTime(health.last_success_at);
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex flex-col cursor-default">
              <span className="inline-flex items-center gap-1 text-sm text-emerald-700 font-medium">
                <span className="h-2 w-2 rounded-full bg-emerald-500 flex-shrink-0" aria-hidden="true" />
                Working
              </span>
              <LastSuccessLine ts={health?.last_success_at} />
            </span>
          </TooltipTrigger>
          {rel && (
            <TooltipContent>
              <p>Last success: {rel}</p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (health.status === "credentials_failing") {
    const rel = relativeTime(health.last_run_at);
    const tipMsg = [
      rel ? `Last attempt: ${rel}` : null,
      health.error_message ? `\u201c${health.error_message}\u201d` : null,
      "Connection skipped until credentials are updated.",
    ]
      .filter(Boolean)
      .join(" \u2014 ");

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex flex-col items-start focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded"
              onClick={() => onOpenEditCredentials(portal)}
              aria-label="Wrong credentials — click to update"
            >
              <span className="inline-flex items-center gap-1 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded px-2 py-0.5 hover:bg-red-100 transition-colors">
                <span aria-hidden="true">&#9888;</span> Wrong creds
              </span>
              <LastSuccessLine ts={health?.last_success_at} />
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p>{tipMsg}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (health.status === "two_fa_failing") {
    const rel = relativeTime(health.last_run_at);
    const twoFaEmail = portal.destination_email || "";
    const tipMsg = [
      rel ? `Last attempt: ${rel}.` : null,
      twoFaEmail
        ? `2FA code didn\u2019t arrive at ${twoFaEmail} or was rejected.`
        : "2FA code didn\u2019t arrive or was rejected.",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex flex-col cursor-default">
              <span className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 w-fit">
                <span aria-hidden="true">&#9888;</span> 2FA didn&rsquo;t arrive
              </span>
              <LastSuccessLine ts={health?.last_success_at} />
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p>{tipMsg}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (health.status === "failing") {
    // Soft, non-actionable pill: this failure class is not the tenant's
    // problem to fix (network blip, portal-side outage, classifier "other"
    // bucket, etc.). Surfacing the raw error_message just generates
    // support tickets for things ops already sees in the cron digest.
    return (
      <span className="inline-flex flex-col">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-1 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-0.5 cursor-default w-fit">
                <span aria-hidden="true">&middot;</span> Sync delayed
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>We&rsquo;re on it. The next cron cycle will retry; nothing for you to do.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <LastSuccessLine ts={health?.last_success_at} />
      </span>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// FundsChip — shows fund count + tooltip with names
// ---------------------------------------------------------------------------

function FundsChip({ portal }) {
  const navigate = useNavigate();
  const funds = portal.funds || [];
  const count = typeof portal.funds_count === "number" ? portal.funds_count : funds.length;
  const truncated = portal.funds_truncated || false;
  const canonicalName = portal.canonical_name || portal.portal_name || "";

  // Only suppress funds when there literally are none. Resolved-custom
  // subscriptions inherit the canonical portal's portal_key, so their
  // funds lookup hits the same fund_positions as the canonical row —
  // previous logic hid funds on any pending_construction row, which
  // unfairly blanked out resolved customs (clockwork, hgi, etc.) that
  // DO have fund data.
  if (count === 0) {
    return <span className="text-gray-400">&mdash;</span>;
  }

  const sortedNames = [...funds]
    .map((f) => f.fund_name || "")
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  const shown = sortedNames.slice(0, 10);
  const remaining = count - shown.length;

  const tooltipBody = [
    ...shown,
    remaining > 0 ? `\u2026 and ${remaining} more` : null,
  ]
    .filter(Boolean)
    .join("\n");

  const chipLabel = count === 1 ? "1 fund" : `${count} funds`;

  const handleClick = () => {
    const portalKey =
      portal.portal_key ||
      (portal.canonical_name ? portal.canonical_name.toLowerCase() : null) ||
      (portal.portal_name ? portal.portal_name.toLowerCase() : null);
    if (portalKey) {
      navigate(`/documents?portal=${encodeURIComponent(portalKey)}`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center text-sm font-medium bg-gray-100 border border-gray-200 rounded px-2 py-0.5 hover:bg-gray-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            aria-label={`${chipLabel} from this portal — view in documents`}
          >
            {chipLabel}
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="font-semibold mb-1">Funds from this connection</p>
          <p className="whitespace-pre-line text-xs">{tooltipBody}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// CredentialsBanner — top-of-page alert when any portal has credentials_failing
// ---------------------------------------------------------------------------

function CredentialsBanner({ portals, onScrollToFirst }) {
  const failing = portals.filter((p) => p.health?.status === "credentials_failing");

  if (failing.length === 0) return null;

  let bodyText;
  let ctaLabel;

  if (failing.length === 1) {
    bodyText = `${failing[0].display_name || failing[0].portal_name} login is failing. Update the credentials to resume document collection.`;
    ctaLabel = "Update now \u2192";
  } else if (failing.length === 2) {
    bodyText = `${failing[0].display_name || failing[0].portal_name} and ${failing[1].display_name || failing[1].portal_name} are failing to log in. Update their credentials to resume document collection.`;
    ctaLabel = "Review all \u2192";
  } else {
    const first = failing[0].display_name || failing[0].portal_name;
    const second = failing[1].display_name || failing[1].portal_name;
    const extra = failing.length - 2;
    bodyText = `${first}, ${second}, and ${extra} more are failing to log in. Update their credentials to resume document collection.`;
    ctaLabel = "Review all \u2192";
  }

  return (
    <div
      role="alert"
      className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex items-start justify-between gap-4"
    >
      <div className="flex items-start gap-3">
        <span className="text-red-600 text-lg leading-none mt-0.5" aria-hidden="true">&#9888;</span>
        <div>
          <p className="font-semibold text-red-800 text-sm">
            {failing.length} portal{failing.length !== 1 ? "s" : ""} need{failing.length === 1 ? "s" : ""} attention
          </p>
          <p className="text-red-700 text-sm mt-0.5">{bodyText}</p>
        </div>
      </div>
      <button
        type="button"
        className="flex-shrink-0 text-sm font-semibold text-white rounded px-3 py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
        style={{ backgroundColor: BRAND.bg }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND.hover)}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND.bg)}
        onClick={onScrollToFirst}
      >
        {ctaLabel}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function CatalogPage() {
  const auth = useAuth();
  const user = auth?.user;
  // Tenants on the shared forwarding pool: one address for every portal.
  const poolAddress =
    !!user?.intake_email && !user.intake_email.toLowerCase().endsWith("@stemmercapital.com");
  const [catalog, setCatalog] = useState([]);
  const [myPortals, setMyPortals] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pause overlay: { [portal_id]: { auth_paused_at, auth_paused_by, auth_paused_reason } }
  // The /my-portals endpoint does not yet return auth_paused_* fields, so we
  // track them locally after a PATCH. This survives re-renders and re-fetches
  // within the session but resets on page reload (backend limitation — BACKLOG).
  const [pauseOverlay, setPauseOverlay] = useState({});

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedPortal, setSelectedPortal] = useState(null);
  const [subscribeDialogOpen, setSubscribeDialogOpen] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPortal, setEditingPortal] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renamingPortal, setRenamingPortal] = useState(null);

  // Refs for scrolling to failing rows
  const rowRefs = useRef({});

  const confirm = useConfirm();

  const handleEditClick = (portal) => {
    setEditingPortal(portal);
    setEditDialogOpen(true);
  };

  const handleRenameClick = (portal) => {
    setRenamingPortal(portal);
    setRenameDialogOpen(true);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [catalogRes, portalsRes] = await Promise.all([
        api.get("/catalog"),
        api.get("/catalog/my-portals"),
      ]);
      setCatalog(catalogRes.data?.portals || []);
      setMyPortals(portalsRes.data?.portals || []);
    } catch (err) {
      toast.error("Failed to load catalog data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Collect BOTH the raw catalog_id (e.g. "custom_d7c03c7e") AND the
  // resolved canonical slug (e.g. "clockwork") from every subscription
  // so the Available Scrapers list correctly treats custom subscriptions
  // as occupying their canonical catalog entry — fixes the bug where
  // Clockwork Admin + iCapital showed as "Available" because the raw
  // catalog_id on the subscription was "custom_xxx".
  const subscribedIds = new Set();
  myPortals.forEach((p) => {
    if (p.catalog_id) subscribedIds.add(p.catalog_id);
    if (p.id) subscribedIds.add(p.id);
    if (p.resolved_catalog_id) subscribedIds.add(p.resolved_catalog_id);
  });

  const availableScrapers = catalog
    .filter((s) => !subscribedIds.has(s.catalog_id))
    .filter((s) => !s.paused)
    .sort((a, b) =>
      (a.name || a.portal_name || "").localeCompare(b.name || b.portal_name || "")
    );

  // Paused portals no longer get their own section — they're shown
  // inside "My Portals" with the Portal Blocked health chip (see
  // HealthChip's blocked branch). The explicit Available-Scrapers
  // filter above (`!s.paused`) already hides them from the "Subscribe
  // to something new" section.

  const handlePauseUpdate = (portalId, fields) => {
    setPauseOverlay((prev) => ({
      ...prev,
      [portalId]: {
        ...(prev[portalId] || {}),
        ...fields,
      },
    }));
  };

  const handleUnsubscribe = async (portal) => {
    const name = portal.display_name || portal.name || portal.portal_name;
    const ok = await confirm({
      title: `Remove ${name}?`,
      description:
        "This stops document collection for this connection. Your existing documents stay; you can re-subscribe later.",
      confirmText: "Remove",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/catalog/my-portals/${portal.portal_id}`);
      toast.success(`Unsubscribed from ${portal.display_name || portal.name || portal.portal_name}`);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to unsubscribe"));
    }
  };

  const handlePickerSelect = (portal) => {
    setSelectedPortal(portal);
    setSubscribeDialogOpen(true);
  };

  const statusBadge = (portal, subscribed) => {
    // Use effectiveStatus so resolved custom subscriptions that stored
    // status=pending_construction but whose canonical scraper is already
    // built render as Active rather than confusingly "Pending".
    const effective =
      portal.catalog_id && typeof effectiveStatus === "function"
        ? effectiveStatus(portal)
        : portal.status;
    if (effective === "pending_construction") {
      return (
        <Badge className="bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50">
          Pending
        </Badge>
      );
    }
    if (subscribed) {
      return (
        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
          Active
        </Badge>
      );
    }
    return <Badge variant="secondary">Available</Badge>;
  };

  const scrollToFirstFailing = () => {
    const firstFailing = sortedMyPortals.find((p) => p.health?.status === "credentials_failing");
    if (!firstFailing) return;
    const key = firstFailing.portal_id || firstFailing.catalog_id;
    const el = rowRefs.current[key];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Brief highlight flash
      el.classList.add("ring-2", "ring-red-400", "ring-inset");
      setTimeout(() => {
        el.classList.remove("ring-2", "ring-red-400", "ring-inset");
      }, 1000);
    }
  };

  // ------------------------------------------------------------------
  // Collapse TRUE duplicate subscriptions — same portal + same host.
  // Group key = (resolved_catalog_id, URL host). A portal with two
  // DIFFERENT entry hosts (e.g. Intralinks at apps.intralinks.com +
  // iam.intralinks.com) is a legitimate multi-workspace subscription
  // and both rows are kept. A portal with two entries on the same host
  // (e.g. HGI case-variant dup) is a data duplicate and we keep only
  // the primary (prefer status=active → non-custom catalog_id →
  // most-recently-updated).
  // ------------------------------------------------------------------
  const hostOf = (url) => {
    if (!url) return "";
    try {
      return new URL(url).hostname.toLowerCase();
    } catch {
      return "";
    }
  };

  const groupedByResolvedSlug = new Map();
  myPortals.forEach((p) => {
    const slug = p.resolved_catalog_id || p.catalog_id;
    if (!slug) return;
    const groupKey = `${slug}||${hostOf(p.url)}`;
    const list = groupedByResolvedSlug.get(groupKey) || [];
    list.push(p);
    groupedByResolvedSlug.set(groupKey, list);
  });

  const primaryPortalIds = new Set();
  for (const rows of groupedByResolvedSlug.values()) {
    const sorted = [...rows].sort((a, b) => {
      const aActive = a.status === "active" ? 0 : 1;
      const bActive = b.status === "active" ? 0 : 1;
      if (aActive !== bActive) return aActive - bActive;
      const aCustom = (a.catalog_id || "").startsWith("custom_") ? 1 : 0;
      const bCustom = (b.catalog_id || "").startsWith("custom_") ? 1 : 0;
      if (aCustom !== bCustom) return aCustom - bCustom;
      // Fall back to most-recently-updated
      return (b.updated_at || "").localeCompare(a.updated_at || "");
    });
    if (sorted[0]?.portal_id) primaryPortalIds.add(sorted[0].portal_id);
  }

  const sortedMyPortals = [...myPortals]
    .filter((p) => primaryPortalIds.has(p.portal_id))
    .sort((a, b) =>
      (a.display_name || a.name || a.portal_name || "").localeCompare(
        b.display_name || b.name || b.portal_name || ""
      )
    )
    .map((p) => {
      const overlay = pauseOverlay[p.portal_id];
      if (!overlay) return p;
      return { ...p, ...overlay };
    });

  // When a custom-request subscription resolves to a canonical slug
  // with a working scraper, the stored status=pending_construction
  // should NOT render as "Pending / Awaiting build" — the scraper
  // IS built, it's just that the Cosmos row never got flipped active.
  // This override is display-only; the underlying doc stays untouched.
  const effectiveStatus = (portal) => {
    const cid = portal.catalog_id || "";
    const resolved = portal.resolved_catalog_id || "";
    const isResolvedCustom = cid.startsWith("custom_") && resolved && resolved !== cid;
    if (isResolvedCustom && portal.status === "pending_construction") {
      return "active";
    }
    return portal.status;
  };

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Connections</h1>
          <p className="text-sm text-gray-500 mt-1">
            Browse available document connections and manage your subscriptions.
          </p>
        </div>
        <Button
          className="text-white"
          style={{ backgroundColor: BRAND.bg }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = BRAND.hover)}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = BRAND.bg)}
          onClick={() => setAddDialogOpen(true)}
        >
          + Add Connection
        </Button>
      </div>

      {/* Credentials failure banner */}
      {!loading && myPortals.length > 0 && (
        <CredentialsBanner
          portals={myPortals}
          onScrollToFirst={scrollToFirstFailing}
        />
      )}


      {/* 2FA senders panel — tenant-wide control, above the portals table */}
      {!poolAddress && <TwoFASendersPanel />}

      {/* My Connections section */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          My Connections
          {!loading && sortedMyPortals.length > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({sortedMyPortals.length})
            </span>
          )}
        </h2>
        {loading ? (
          <div className="rounded-lg border bg-white">
            <div className="p-4 space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-8 w-20 ml-auto" />
                </div>
              ))}
            </div>
          </div>
        ) : myPortals.length === 0 ? (
          <div className="rounded-lg border bg-white p-8 text-center">
            <p className="text-sm text-gray-500">
              You have no active subscriptions yet. Click "Add Connection" to get started.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[48px] text-center text-gray-400">#</TableHead>
                  <TableHead className="w-[22%]">Portal</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>2FA Email</TableHead>
                  <TableHead>Health</TableHead>
                  <TableHead>Funds</TableHead>
                  <TableHead>Pause</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedMyPortals.map((portal, idx) => {
                  const key = portal.portal_id || portal.catalog_id;
                  const displayName = portal.display_name || portal.name || portal.portal_name;
                  const canonicalName = portal.canonical_name || portal.portal_name || portal.name;
                  const showCanonical =
                    displayName && canonicalName && displayName !== canonicalName;

                  return (
                    <TableRow
                      key={key}
                      ref={(el) => {
                        if (el) rowRefs.current[key] = el;
                        else delete rowRefs.current[key];
                      }}
                      className="transition-all duration-300"
                    >
                      {/* Row number — so the client can see at a
                          glance how many portals they have. */}
                      <TableCell className="text-center text-sm text-gray-400 tabular-nums">
                        {idx + 1}
                      </TableCell>

                      {/* Portal name cell */}
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <PortalLogo portal={portal} />
                          <div className="flex flex-col min-w-0">
                            <span className="truncate">
                              {displayName}
                              {showCanonical && (
                                <span className="text-gray-500 font-normal ml-1">
                                  ({canonicalName})
                                </span>
                              )}
                            </span>
                            {portal.description ? (
                              <span className="text-xs text-gray-500 font-normal truncate">
                                {portal.description}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </TableCell>

                      {/* URL */}
                      <TableCell className="text-gray-600 text-sm">
                        {portal.url ? (
                          <a
                            href={portal.url}
                            target="_blank"
                            rel="noreferrer"
                            className="underline truncate block max-w-[200px]"
                            title={portal.url}
                          >
                            {hostname(portal.url)}
                          </a>
                        ) : (
                          <span className="text-gray-400">&mdash;</span>
                        )}
                      </TableCell>

                      {/* Username */}
                      <TableCell
                        className="text-gray-700 text-sm truncate max-w-[160px]"
                        title={portal.username || ""}
                      >
                        {portal.username || <span className="text-gray-400">&mdash;</span>}
                      </TableCell>

                      {/* 2FA Email */}
                      <TableCell
                        className="text-gray-700 text-sm truncate max-w-[180px]"
                        title={(poolAddress ? user.intake_email : portal.destination_email) || ""}
                      >
                        {(poolAddress ? user.intake_email : portal.destination_email) || (
                          <span className="text-gray-400">&mdash;</span>
                        )}
                      </TableCell>

                      {/* Health chip */}
                      <TableCell>
                        <HealthChip
                          portal={portal}
                          onOpenEditCredentials={handleEditClick}
                        />
                      </TableCell>

                      {/* Funds chip */}
                      <TableCell>
                        <FundsChip portal={portal} />
                      </TableCell>

                      {/* Pause toggle */}
                      <TableCell>
                        <PortalPauseToggle
                          portal={portal}
                          onUpdate={(fields) => handlePauseUpdate(portal.portal_id, fields)}
                        />
                      </TableCell>

                      {/* Actions dropdown */}
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="outline" size="sm">
                                Edit &#9660;
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onSelect={() => handleEditClick(portal)}
                                className="cursor-pointer"
                              >
                                Edit credentials
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() => handleRenameClick(portal)}
                                className="cursor-pointer"
                              >
                                Rename
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUnsubscribe(portal)}
                          >
                            Remove
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {/* Available Connections section */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Available Connections
        </h2>
        {loading ? (
          <div className="rounded-lg border bg-white">
            <div className="p-4 space-y-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-8 w-20 ml-auto" />
                </div>
              ))}
            </div>
          </div>
        ) : availableScrapers.length === 0 ? (
          <div className="rounded-lg border bg-white p-8 text-center">
            <p className="text-sm text-gray-500">
              You are subscribed to all available connections.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40%]">Name</TableHead>
                  <TableHead className="w-[30%]">Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {availableScrapers.map((portal) => {
                  return (
                    <TableRow key={portal.catalog_id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <PortalLogo portal={portal} />
                          <span>{portal.name || portal.portal_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {portal.description || "Automated document scraping"}
                      </TableCell>
                      <TableCell>{statusBadge(portal, false)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          className="text-white"
                          style={{ backgroundColor: BRAND.bg }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = BRAND.hover)
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor = BRAND.bg)
                          }
                          onClick={() => handlePickerSelect(portal)}
                        >
                          Subscribe
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {/* Add Scraper choice dialog */}
      <AddScraperDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onChooseCatalog={() => {
          setSelectedPortal(null);
          setSubscribeDialogOpen(true);
        }}
        onChooseCustom={() => setRequestDialogOpen(true)}
      />

      {/* Subscribe credentials dialog */}
      <SubscribeDialog
        portal={selectedPortal}
        availableScrapers={availableScrapers}
        open={subscribeDialogOpen}
        onOpenChange={setSubscribeDialogOpen}
        onSuccess={fetchData}
      />

      {/* Request custom portal dialog */}
      <RequestPortalDialog
        open={requestDialogOpen}
        onOpenChange={setRequestDialogOpen}
        onSuccess={fetchData}
      />

      {/* Edit credentials dialog */}
      <EditCredentialsDialog
        portal={editingPortal}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSuccess={fetchData}
      />

      {/* Rename dialog */}
      <RenamePortalDialog
        portal={renamingPortal}
        open={renameDialogOpen}
        onOpenChange={setRenameDialogOpen}
        onSuccess={fetchData}
      />
    </div>
  );
}
